import axios from 'axios';
import { useQuery, useMutation } from 'react-query';

export const useFetchClientDetails = (token) => {
    return useQuery(['clientDetails', token], async () => {
        const response = await axios.get('/api/client-details', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    }, {
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

export const useClientPendingInvitesCount = (token) => {
    return useQuery(['clientPendingInvitesCount', token], async () => {
        const response = await axios.get(`/api/client/get-pending-invites-count`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data.cli_pending_invites;
    }, {
        staleTime: 2 * 60 * 1000,
        cacheTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

export const useFetchClientExportDetails = (token, enabled = false) => {
    return useQuery(['clientExportDetails', token], async () => {
        const response = await axios.get('/api/client/export/details', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    }, {
        enabled,
        staleTime: 5 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

export function useClientView(clientId, token) {
    return useQuery(
        ['clientView', token, clientId],
        async () => {
            if (!clientId) {
                return null;
            }
            const response = await axios.get(`/api/client-details/${clientId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        },
        {
            enabled: !!clientId,
            staleTime: 3 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
        }
    );
}

export function usePosition(token) {
    return useQuery(['position', token], async () => {
        const response = await axios.get('/api/position-details', {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    }, {
        staleTime: 10 * 60 * 1000,
        cacheTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!token,
    });
}

export function useDeleteClientDetails(token) {
    const mutation = useMutation((selectedId) => {
        return axios.get(`/api/client-details/delete/${selectedId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    });

    return mutation;
}

export function useUpdateClientDetails(token) {
    const mutation = useMutation((updatedClientDetails) => {
        return axios.put(`/api/client-details/update/${updatedClientDetails.selectedId}`, updatedClientDetails.data, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    });

    return mutation;
}

export function useUpdateAccManager(token) {
    const mutation = useMutation((updatedAccManager) => {
        return axios.put(`/api/clients/update-acc-manager/${updatedAccManager.id}`,{acc_manager: updatedAccManager.acc_manager}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    });

    return mutation;
}

export function useUpdateProjManager(token) {
    const mutation = useMutation((updatedProjManager) => {
        return axios.put(`/api/client/update-project-managers/${updatedProjManager.id}`,{projectManagers: updatedProjManager.projectManagers, previousProjectManager: updatedProjManager.previousProjectManager}, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    });

    return mutation;
}

export function useRemoveTeamMember(token) {
    const mutation = useMutation((removedTeamMember) => {
        return axios.put(`/api/client/remove-project-manager/${removedTeamMember.id}`,{projectManagerId: removedTeamMember.teamMember }, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
    });

    return mutation;
}